import {IMeasurementsStatisticsFilter} from './measurements-statistics-filter.interface';
import {cloneDeep, uniqBy} from 'lodash';
import {BaseService, DataBaseServiceResponse, ServiceLoopbackFilters} from '../../services/base-service/base.service';
import {Injectable} from '@angular/core';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {IMeasurement} from './measurement.interface';
import {
  IMeasurementHeaders,
  MeasurementTemplateRequestDTO,
  PatientMeasurementCreatorDTO,
  PatientMeasurementRequestDTO,
  PatientMeasurementResponseDTO,
  PatientMeasurementSystemRequestDTO
} from '../../dtos/measurement';
import {IMeasurementRow, MeasurementRowEnum} from '../../components/new-measurement/new-measurement.component';
import {IPatientMeasurement} from './patient-measurement.interface';
import {IMeasurementTemplate, IMeasurementTemplateObject} from './measurement-template.interface';
import {IAnalysisMeasurement} from './analysis-measurement.interface';
import {firstValueFrom} from 'rxjs';
import {environment} from '../../../environments/environment';
import {IMeasureStatistics} from './measure-statistics.interface';
import {AnyObject} from '../../interfaces/utils';

export interface IRMeasurement {
  categories: any[];
  measurementsType: IMeasurement[];
  measurementCreated?: IPatientMeasurement;
  measurementsHistorial: PatientMeasurementResponseDTO;
  measurementsHistorialHeaders: IMeasurementHeaders[];
  registerInfo?: PatientMeasurementCreatorDTO;
  measurementsRow: IMeasurementRow[];
  measurementsTemplate: IMeasurementTemplate[];
  analysisMeasurements?: IAnalysisMeasurement;
  measurementsStatistics?: IMeasureStatistics[];
}


export namespace MeasurementAction {

  export class Get {
    static readonly type: string = '[MeasurementState] Get Measurements Action';
    constructor(public filters?: ServiceLoopbackFilters, public appendData = false) { }
  }

  export class GetPatientMeasurements {
    static readonly type: string = '[MeasurementState] Get patient measurements Action';
    constructor(public clinicId: string, public patientId: string, public expertId: string, public serviceId: string) { }
  }

  export class GetPatientMeasurementHeaders {
    static readonly type: string = '[MeasurementState] Get Measurements Headers Action';
    constructor(public patientId: string, public serviceId: string, public withoutRepeating: boolean = false) { }
  }

  export class Reset {
    static readonly type: string = '[MeasurementState] Reset Measurements Action';
    constructor() { }
  }

  export class Post {
    static readonly type: string = '[MeasurementState] Post Measurements Action';
    constructor(
      public clinicId: string,
      public patientId: string,
      public expertId: string,
      public patientMeasurement: PatientMeasurementRequestDTO
    ) { }
  }

  export class GetPatientMeasurementCreator {
    static readonly type: string = '[MeasurementState] Get Register Info Action';
    constructor(
      public clinicId: string,
      public patientId: string,
      public expertId: string,
      public patientMeasurementId: string
    ) { }
  }

  export class ChangeStatusToSavedMeasurementTemporal {
    static readonly type: string = '[MeasurementState] Change Status To Saved Measurement Temporal';
    constructor() { }
  }

  export class AddMeasurementTemporal {
    static readonly type: string = '[MeasurementState] Ad dMeasurement Temporal Action';
    constructor(public measurement: IMeasurementRow) { }
  }

  export class RemoveMeasurementTemporal {
    static readonly type: string = '[MeasurementState] Remove dMeasurement Temporal Action';
    constructor(public measurementId: string) { }
  }

  export class Put {
    static readonly type: string = '[MeasurementState] Put Measurements Action';
    constructor(
      public clinicId: string,
      public patientId: string,
      public expertId: string,
      public data: { patientMeasurementId: string, measureUnitId: string, value: number, measurementId: string }[]) { }
  }

  export class DeleteMeasurementRow {
    static readonly type: string = '[MeasurementState] Delete Measurement Row Action';
    constructor(
      public clinicId: string,
      public patientId: string,
      public expertId: string,
      public patientMeasurementId: string,
    ) { }
  }

  export class PostTemplateMeasurements {
    static readonly type: string = '[MeasurementState] Post Template Measurements Action';
    constructor(
      public expertId: string,
      public data: MeasurementTemplateRequestDTO
    ) { }
  }

  export class PatchTemplateMeasurements {
    static readonly type: string = '[MeasurementState] Patch Template Measurements Action';
    constructor(
      public expertId: string,
      public measurementTemplateId: string,
      public data: MeasurementTemplateRequestDTO
    ) { }
  }

  export class GetTemplatesMeasurements {
    static readonly type: string = '[MeasurementState] Get Templates Measurements Action';
    constructor(
      public expertId: string
    ) { }
  }

  export class MeasurementTemplateAsDefault {
    static readonly type: string = '[MeasurementState] Measurement Template As Default Action';
    constructor(
      public expertId: string,
      public measurementTemplateId: string
    ) { }
  }

  export class DeleteMeasurementTemplate {
    static readonly type: string = '[MeasurementState] Delete Measurement Template Action';
    constructor(
      public expertId: string,
      public measurementTemplateId: string
    ) { }
  }

  export class ResetMeasurementTemplate {
    static readonly type: string = '[MeasurementState] Reset Measurement Template Action';
    constructor() { }
  }

  export class GetAvailableFormulas {
    static readonly type: string = '[MeasurementState] Get Available Formulas Action';
    constructor(public serviceId: string, public patientId: string, public expertId: string) {
    }
  }

  export class PostAvailableFormulas {
    static readonly type: string = '[MeasurementState] Post Available Formulas Action';
    constructor(public serviceId: string, public patientId: string, public expertId: string, public data: PatientMeasurementSystemRequestDTO) {
    }
  }

  export class GetMeasurementStatistics {
    static readonly type: string = '[MeasurementState] Get Measurement Statistics Action';
    constructor(
      public serviceId: string,
      public patientId: string,
      public queryParams?: IMeasurementsStatisticsFilter
    ) { }
  }
}


@State<IRMeasurement>({
  name: 'measurementState',
  defaults: {
    categories: [],
    measurementsHistorial: null,
    measurementsHistorialHeaders: [],
    measurementsType: [],
    measurementsRow: [],
    measurementsTemplate: []
  },
})

@Injectable()
export class MeasurementState {
  constructor(
    private baseService: BaseService<any>
  ) { }

  url = environment.SERVER_URL;

  @Selector()
  public static getCategories(state: IRMeasurement): any[] {
    return state.categories;
  }

  @Selector()
  public static getMeasurementsType(state: IRMeasurement): IMeasurement[] {
    return state.measurementsType;
  }

  @Selector()
  public static getMeasurementsHistorial(state: IRMeasurement): PatientMeasurementResponseDTO {
    return state.measurementsHistorial;
  }

  @Selector()
  public static getMeasurementsHistorialHeaders(state: IRMeasurement): IMeasurementHeaders[] {
    return state.measurementsHistorialHeaders;
  }

  @Selector()
  public static getRegisterInfo(state: IRMeasurement): PatientMeasurementCreatorDTO {
    return state.registerInfo;
  }

  @Selector()
  public static getMeasurementsTemp(state: IRMeasurement): IMeasurementRow[] {
    return state.measurementsRow;
  }

  @Selector()
  public static getMeasurementCreated(state: IRMeasurement): IPatientMeasurement {
    return state.measurementCreated;
  }

  @Selector()
  public static getMeasurementsTemplate(state: IRMeasurement): IMeasurementTemplate[] {
    return state.measurementsTemplate;
  }

  @Selector()
  public static getAnalysisMeasurement(state: IRMeasurement): IAnalysisMeasurement {
    return state.analysisMeasurements;
  }

  @Selector()
  public static getMeasureStatistics(state: IRMeasurement): IMeasureStatistics[] {
    return state.measurementsStatistics;
  }

  @Action(MeasurementAction.Get)
  async getMeasurementsAction(ctx: StateContext<IRMeasurement>, action: MeasurementAction.Get): Promise<void> {
    const response: DataBaseServiceResponse<IMeasurement[]> = await firstValueFrom((this.baseService.get(`${this.url}/measurements`, action.filters)));
    if (response.error) throw new Error(response.message);

    ctx.patchState({
      measurementsType: !action.appendData ? response.entity : [...ctx.getState().measurementsType, ...response.entity],
    });
  }

  @Action(MeasurementAction.ChangeStatusToSavedMeasurementTemporal)
  async changeStatusToSavedMeasurementTemporal({patchState, getState}: StateContext<IRMeasurement>): Promise<void> {
    patchState({
      measurementsRow: [
        ...(getState().measurementsRow ?? []).map((measurementRow: IMeasurementRow): IMeasurementRow => ({...measurementRow, saved: true}))],
    });
  }

  @Action(MeasurementAction.AddMeasurementTemporal)
  async addMeasurementTemporalAction(ctx: StateContext<IRMeasurement>, action: MeasurementAction.AddMeasurementTemporal): Promise<void> {
    const measurementsTemp: IMeasurementRow[] = cloneDeep(ctx.getState().measurementsRow);

    const index = measurementsTemp.findIndex((item: IMeasurementRow) => item._id === action.measurement._id);
    if (index !== -1) {
      measurementsTemp[index] = {
        ...action.measurement,
        saved: false,
      };
    } else {
      measurementsTemp.push({
        ...action.measurement,
        saved: false,
      });
    }

    ctx.patchState({
      measurementsRow: [...measurementsTemp]
    });
  }

  @Action(MeasurementAction.RemoveMeasurementTemporal)
  async removeMeasurementTemporalAction(ctx: StateContext<IRMeasurement>, action: MeasurementAction.RemoveMeasurementTemporal): Promise<void> {
    const measurementsTemp = cloneDeep(ctx.getState().measurementsRow);

    const index = measurementsTemp.findIndex(item => item._id === action.measurementId);
    if (index !== -1) {
      measurementsTemp.splice(index, 1);
    }

    ctx.patchState({
      measurementsRow: [...measurementsTemp]
    });
  }

  @Action(MeasurementAction.GetPatientMeasurements)
  async getMeasurementsToFilterAction(ctx: StateContext<IRMeasurement>, action: MeasurementAction.GetPatientMeasurements): Promise<void> {
    const response: DataBaseServiceResponse<PatientMeasurementResponseDTO> = await firstValueFrom(
      this.baseService.get(`${this.url}/patients/${action.patientId}/service/${action.serviceId}/measurements`)
    );

    if (response.error) throw new Error(response.message);
    ctx.patchState({
      measurementsHistorial: response.entity
    });
  }

  @Action(MeasurementAction.GetPatientMeasurementHeaders)
  async getMeasurementsHeadersAction(ctx: StateContext<IRMeasurement>, action: MeasurementAction.GetPatientMeasurementHeaders): Promise<void> {
    const response: DataBaseServiceResponse<IMeasurementHeaders[]> = await firstValueFrom(
      this.baseService.get(`${this.url}/patients/${action.patientId}/service/${action.serviceId}/measurements/headers`)
    );

    if (response.error) throw new Error(response.message);

    if (action.withoutRepeating) {
      response.entity = uniqBy(response.entity, 'category');
    }

    ctx.patchState({
      measurementsHistorialHeaders: response.entity
    });
  }

  @Action(MeasurementAction.Post)
  async postMeasurementsAction(ctx: StateContext<IRMeasurement>, action: MeasurementAction.Post): Promise<void> {
    const response: DataBaseServiceResponse<IPatientMeasurement> = await firstValueFrom(
      this.baseService.post(`${this.url}/patients/${action.patientId}/experts/${action.expertId}/measurements`, action.patientMeasurement)
    );

    ctx.patchState({
      measurementCreated: response.entity[0]
    });
  }

  @Action(MeasurementAction.GetPatientMeasurementCreator)
  async getRegisterInfoAction(ctx: StateContext<IRMeasurement>, action: MeasurementAction.GetPatientMeasurementCreator): Promise<void> {
    const response: DataBaseServiceResponse<PatientMeasurementCreatorDTO> = await firstValueFrom(
      this.baseService.get(`${this.url}/patients/${action.patientId}/experts/${action.expertId}/patient-measurements/${action.patientMeasurementId}/creator`)
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({registerInfo: response.entity});
  }

  @Action(MeasurementAction.Put)
  async putMeasurementsAction(ctx: StateContext<IRMeasurement>, action: MeasurementAction.Put): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.put(`${this.url}/patients/${action.patientId}/experts/${action.expertId}/measurements`, { measurements: action.data })
    );

    if (response.error) throw new Error(response.message);
  }

  @Action(MeasurementAction.DeleteMeasurementRow)
  async deleteMeasurementRowAction(ctx: StateContext<IRMeasurement>, action: MeasurementAction.DeleteMeasurementRow): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.delete(`${this.url}/patients/${action.patientId}/experts/${action.expertId}/patient-measurements/${action.patientMeasurementId}`)
    );

    if (response.error) throw new Error(response.message);
  }

  @Action(MeasurementAction.Reset)
  async resetMeasurementsAction(ctx: StateContext<IRMeasurement>, _: MeasurementAction.Reset): Promise<void> {
    ctx.patchState({
      measurementsRow: [],
      categories: [],
      measurementCreated: null,
      measurementsHistorial: null,
      measurementsHistorialHeaders: [],
      measurementsType: [],
      registerInfo: null,
    });
  }


  @Action(MeasurementAction.PostTemplateMeasurements)
  async postTemplateMeasurementsAction(ctx: StateContext<IRMeasurement>, action: MeasurementAction.PostTemplateMeasurements): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom( // Este método debería regresar un objeto
      this.baseService.post(`${this.url}/experts/${action.expertId}/measurement-templates`, action.data)
    );

    if (response.error) throw new Error(response.message);
  }

  @Action(MeasurementAction.PatchTemplateMeasurements)
  async patchTemplateMeasurementsAction(ctx: StateContext<IRMeasurement>, action: MeasurementAction.PatchTemplateMeasurements): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.patch(`${this.url}/experts/${action.expertId}/measurement-templates/${action.measurementTemplateId}`, action.data)
    );

    if (response.error) throw new Error(response.message);
  }

  @Action(MeasurementAction.GetTemplatesMeasurements)
  async getTemplatesMeasurementsAction(ctx: StateContext<IRMeasurement>, action: MeasurementAction.GetTemplatesMeasurements): Promise<void> {
    const response: DataBaseServiceResponse<IMeasurementTemplate[]> = await firstValueFrom(
      this.baseService.get(`${this.url}/experts/${action.expertId}/measurement-templates`)
    );

    if (response.error) throw new Error(response.message);

    const measurementsTemplate: IMeasurementTemplate[] = response.entity.map((template: IMeasurementTemplate): IMeasurementTemplate => ({
      ...template,
      measurementsRow: Array.isArray(template.measurements) ? (template.measurements.map((measure: IMeasurementTemplateObject): IMeasurementRow => ({
        status: MeasurementRowEnum.FILL,
        name: measure.measurement.name_es,
        value: null,
        unitMeasurement: measure.measurement?.measureUnits,
        unitMeasurementDefault: null,
        unit: measure.defaultMeasureUnitId,
        _id: measure.measurementId,
        measurementSelected: measure.measurement,
        showEdit: true,
        fromTemplate: true
      }))) : []
    }));
    ctx.patchState({measurementsTemplate});
  }

  @Action(MeasurementAction.MeasurementTemplateAsDefault)
  async measurementTemplateAsDefaultAction(ctx: StateContext<IRMeasurement>, action: MeasurementAction.MeasurementTemplateAsDefault): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(this.baseService.patch(
      `${this.url}/experts/${action.expertId}/measurement-templates/${action.measurementTemplateId}/set-default`, void 0
    ));

    if (response.error) throw new Error(response.message);
  }

  @Action(MeasurementAction.DeleteMeasurementTemplate)
  async deleteMeasurementTemplateAction(ctx: StateContext<IRMeasurement>, action: MeasurementAction.DeleteMeasurementTemplate): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.delete(`${this.url}/experts/${action.expertId}/measurement-templates/${action.measurementTemplateId}`)
    );

    if (response.error) throw new Error(response.message);
  }

  @Action(MeasurementAction.ResetMeasurementTemplate)
  async resetMeasurementTemplateAction(ctx: StateContext<IRMeasurement>, _: MeasurementAction.ResetMeasurementTemplate): Promise<void> {
    ctx.patchState({
      categories: [],
      measurementsHistorial: null,
      measurementsHistorialHeaders: [],
      measurementsType: [],
      measurementsRow: [],
      measurementsTemplate: [],
      measurementCreated: null
    });
  }

  @Action(MeasurementAction.GetAvailableFormulas)
  async getAvailableFormulasAction(ctx: StateContext<IRMeasurement>, action: MeasurementAction.GetAvailableFormulas): Promise<void> {
    const response: DataBaseServiceResponse<IAnalysisMeasurement> = await firstValueFrom(
      this.baseService.get(`${this.url}/patients/${action.serviceId}/${action.patientId}/${action.expertId}/availableFormulas`, void 0)
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({analysisMeasurements: response.entity});
  }

  @Action(MeasurementAction.PostAvailableFormulas)
  async postAvailableFormulasAction(
    ctx: StateContext<IRMeasurement>,
    {serviceId, patientId, data, expertId}: MeasurementAction.PostAvailableFormulas
  ): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom( // Esté método debería devolver un objeto
      this.baseService.post(`${this.url}/patients/${serviceId}/${patientId}/${expertId}/availableFormulas/patientMeasurements`, data)
    );

    if (response.error) throw new Error(response.message);
  }

  @Action(MeasurementAction.GetMeasurementStatistics)
  async getMeasurementStatisticsAction(ctx: StateContext<IRMeasurement>, action: MeasurementAction.GetMeasurementStatistics): Promise<void> {
    const options: AnyObject = {
      params: {
        query: JSON.stringify({
          measurementIds: action.queryParams.measurementIds,
          range: action.queryParams.range
        })
      }
    };

    const response: DataBaseServiceResponse<IMeasureStatistics[]> = await firstValueFrom(
      this.baseService.get(`${this.url}/patients/${action.patientId}/service/${action.serviceId}/measurements/statistics`, void 0, void 0, options)
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      measurementsStatistics: response.entity
    });
  }
}
